/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import { detectOS } from 'detect-browser';
import { Maybe } from 'purify-ts';
import Vue from 'vue';
import { array, assert, helpers, object, typeHelpers } from '@codegrade/utils';
import { range } from './range';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function emptyVNode() {
    return { isRootInsert: false, isComment: true, text: '' };
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function renderSlot(
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
vm, name, scope, 
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
mapper = n => n, dflt = emptyVNode) {
    return Maybe.fromNullable(vm.$scopedSlots[name])
        .chainNullable(slot => slot(scope))
        .map(mapper)
        .orDefaultLazy(() => dflt(scope));
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function maybeRenderScopedSlot(scope, slot) {
    return Maybe.fromNullable(slot?.(scope));
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function renderScopedSlot(scope, slot, dflt) {
    const res = maybeRenderScopedSlot(scope, slot);
    return res.orDefaultLazy(() => dflt(scope));
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function vueSet(obj, key, value) {
    // @ts-expect-error: According to vue this cannot be called with
    // `symbol` as key, but that is a possible value for `keyof Y`
    return Vue.set(obj, key, value);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function vueSetDefault(obj, key, value) {
    if (object.hasAttr(obj, key)) {
        return vueSet(obj, key, value);
    }
    return value;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function vueDelete(obj, key) {
    // @ts-expect-error: According to vue this cannot be called with
    // `symbol` as key, but that is a possible value for `keyof Y`
    return Vue.delete(obj, key);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function innerRenderRow(h, columns, opts) {
    // Make sure that for every combination of rows we are actually
    // able to find a correct bootstrap class.
    if (!IS_PRODUCTION) {
        const renderRowOptsKeys = typeHelpers.keysOfFast(['amountDefault', 'amountXLarge', 'amountLarge', 'amountMedium']);
        assert.AssertionError.assert(renderRowOptsKeys.every(amountPerDim => range(1, Math.min(columns.length, opts[amountPerDim] ?? 1) + 1).every(amount => Math.floor(12 / amount) === 12 / amount)));
    }
    const cols = Maybe.catMaybes(columns);
    if (cols.length === 0) {
        return Maybe.empty();
    }
    const amountCols = cols.length;
    const amountDefault = Math.min(amountCols, opts.amountDefault ?? 1);
    const amountMedium = Math.min(amountCols, opts.amountMedium ?? amountDefault);
    const amountLarge = Math.min(amountCols, opts.amountLarge ?? amountMedium);
    const amountXLarge = Math.min(amountCols, opts.amountXLarge ?? amountLarge);
    const colOpts = [
        ['md', amountMedium],
        ['lg', amountLarge],
        ['xl', amountXLarge],
    ].reduce((acc, [variant, amount]) => {
        if (array.last(acc)[1] !== amount) {
            acc.push([variant, amount]);
        }
        return acc;
    }, [[undefined, amountDefault]]);
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    const join = (...args) => args.filter(x => x != null).join('-');
    const baseClasses = colOpts.map(([variant, amount]) => join('col', variant, Math.floor(12 / amount).toString(10)));
    // Add margins to all divs that are not on the last row.
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    const getMargins = (idx) => colOpts.map(([variant, amount]) => {
        const marginAmount = idx + amount < amountCols ? 3 : 0;
        return join('mb', variant, marginAmount.toString(10));
    });
    return Maybe.of(cols.map((col, idx) => {
        const classes = [baseClasses, getMargins(idx)];
        return h('div', { class: classes }, [col]);
    }));
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function renderRow(h, columns, opts) {
    return innerRenderRow(h, columns, opts).map(content => h('div', { class: 'row mb-3' }, content));
}
// Tabindices are global across the page, so this gives out bases for the
// tabindex in chunks of 1000. If a component needs more than 1000 tabindices it
// should request multiple bases.
const nextTabindex = helpers.makeUniqueCounter();
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function getTabindexBase() {
    return 1000 * nextTabindex();
}
const isCtrlOrCmd = (() => {
    // We will handle the meta key on Mac or if the OS could not be determined.
    let shouldHandleMeta = true;
    try {
        const os = detectOS(navigator.userAgent);
        shouldHandleMeta = os == null || os === 'Mac OS';
    }
    catch {
        // DO NOTHING...
    }
    return (event) => {
        if (event.ctrlKey) {
            return true;
        }
        if (event.metaKey) {
            return shouldHandleMeta;
        }
        return false;
    };
})();
// Call an event handler for a KeyboardEvent only when either ctrl or cmd was
// pressed in combination with any of the keys in the `key` argument. The
// values in `key` must be those in the first column of the table at
// https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key/Key_Values
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function ctrlOrCmd(key, handler) {
    return (event) => {
        if (isCtrlOrCmd(event) && event.key === key) {
            return handler(event);
        }
        return undefined;
    };
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function doubleRequestAnimationFrame(cb) {
    return new Promise(resolve => {
        Vue.nextTick(() => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (cb != null) {
                        cb();
                    }
                    resolve();
                });
            });
        });
    });
}
/**
 * Useful when doing async requests in a vue component. This function calls the
 * provided `getter` to store some data in its closure once its called. It
 * returns a function that accepts yet another function `fun` and arguments for
 * `fun` and, when called, will call the `getter` again and only call `fun` with
 * the provided arguments if the data returned by `getter` was unchanged between
 * the calls to it.
 *
 * @param getter The getter for the data that might change.
 * @returns A function that will only be called when the return value of
 * `getter` was unchanged.
 */
export function ifDataUnchanged(getter) {
    const initialData = array.ensureArray(getter());
    return fun => 
    /**
     * A function that calls `fun` if the data is unchanged.
     *
     * @param args Arguments passed on to the wrapped function.
     */
    (...args) => {
        const newData = array.ensureArray(getter());
        if (initialData.length === newData.length &&
            initialData.every((el, i) => newData[i] === el)) {
            fun(...args);
        }
    };
}
